import "./styles.scss";

import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input, Button, CircularProgress } from "@nextui-org/react";
import { FiArrowRight } from "react-icons/fi";
import { useSelector } from "react-redux";
import { BsPlus } from "react-icons/bs";
import { LuChevronsUpDown } from "react-icons/lu";
import { IoIosCheckmarkCircleOutline, IoMdSend } from "react-icons/io";
import { PiWarningCircle } from "react-icons/pi";

import MHoverToggleMenu from "../../../components/Kit/MHoverToggleMenu";

import myAxios from "../../../utils/axiosWrapper";
import { auth } from "../../../conf/firebase";
import getConfig from "../../../conf/config";

const InvitationModal = ({ handleOnClose = () => {}, company_config, existingOperator = null }) => {
  const { user } = useSelector((state) => state.user);
  const { currentUser } = auth;
  const { t } = useTranslation();

  const isLinkingExistingOperator = existingOperator !== null;

  const [firstnameInput, setFirstNameInput] = useState("");
  const [lastnameInput, setLastNameInput] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [extensionInput, setExtensionInput] = useState("");
  const [extensionAircallInput, setExtensionAircallInput] = useState("");
  const [cxOneIdentifierInput, setCxOneIdentifierInput] = useState("");
  const [phoneNumberInput, setPhoneNumberInput] = useState("");
  const [creationInputName, setCreationInputName] = useState("");
  const [currentStep, setCurrentStep] = useState("infos");

  const is3cx = company_config?.cx_provider === "3cx";
  const isAircall = company_config?.cx_provider === "aircall";
  const isSftpHsf = company_config?.cx_provider === "sftp_hsf";

  const [selectedDptId, setSelectedDptId] = useState(null);
  const [selectedTeamId, setSelectedTeamId] = useState(null);
  const [selectedLocId, setSelectedLocId] = useState(null);
  const [organisations, setOrganisations] = useState({});
  const [isOrganisationsLoading, setIsOrganisationsLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExtensionCheckLoading, setIsExtensionCheckLoading] = useState(false);
  const [isExtensionAvailable, setIsExtensionAvailable] = useState(false);

  // Pre-fill form when linking existing operator
  useEffect(() => {
    if (existingOperator) {
      setFirstNameInput(existingOperator.first_name || "");
      setLastNameInput(existingOperator.last_name || "");
      setEmailInput(existingOperator.email || "");
    }
  }, [existingOperator]);

  const isEmailValid = emailInput?.match(
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/
  );

  // Validation for new member flow
  const isStep1Valid =
    firstnameInput?.length > 0 &&
    lastnameInput?.length > 0 &&
    emailInput?.length > 0 &&
    isEmailValid;

  const isExtension3cxNotEmpty = extensionInput?.length !== 0 && extensionInput !== "-";
  const isExtensionAircallNotEmpty =
    extensionAircallInput?.length !== 0 && extensionAircallInput !== "-";
  const isCxOneIdentifierNotEmpty =
    cxOneIdentifierInput?.length !== 0 && cxOneIdentifierInput !== "-";
  const isExtension3cxValid =
    isExtension3cxNotEmpty && (extensionInput === -1 || isExtensionAvailable);
  const isExtensionAircallValid = isExtensionAircallNotEmpty;
  const isCxOneIdentifierValid = isCxOneIdentifierNotEmpty && isExtensionAvailable;

  const isExtensionValid =
    (is3cx && isExtension3cxValid) ||
    (isAircall && isExtensionAircallValid) ||
    (isSftpHsf && isCxOneIdentifierValid);

  const isStep2Valid =
    selectedDptId !== null && selectedTeamId !== null && selectedLocId !== null && isExtensionValid;

  // Validation for existing operator flow
  const isExistingOperatorFormValid =
    firstnameInput?.length > 0 &&
    lastnameInput?.length > 0 &&
    emailInput?.length > 0 &&
    isEmailValid;

  const checkExtensionAvailability = async (extension) => {
    setIsExtensionCheckLoading(true);
    setIsExtensionAvailable(false);
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/check_extension_3cx/${extension}`
    );
    const isExtensionAvailableRes = response?.data?.[0]?.is_valid;
    setIsExtensionAvailable(isExtensionAvailableRes);

    setIsExtensionCheckLoading(false);
  };

  const checkCxOneIdentifierAvailability = async (identifier) => {
    setIsExtensionCheckLoading(true);
    setIsExtensionAvailable(false);
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/check_cx_one_identifier/${identifier}`
    );
    const isAvailableRes = response?.data?.[0]?.is_valid;
    setIsExtensionAvailable(isAvailableRes);
    setIsExtensionCheckLoading(false);
  };

  useEffect(() => {
    if (extensionInput === "-1") {
      setIsExtensionAvailable(true);
      return;
    }
    if (extensionInput?.length <= 0 || extensionInput === "-") {
      return;
    }
    checkExtensionAvailability(extensionInput);
  }, [extensionInput]);

  useEffect(() => {
    if (cxOneIdentifierInput?.length <= 0 || cxOneIdentifierInput === "-") {
      return;
    }
    checkCxOneIdentifierAvailability(cxOneIdentifierInput);
  }, [cxOneIdentifierInput]);

  const fetchCompanyOrganisations = async () => {
    setIsOrganisationsLoading(true);
    const response = await myAxios.get(
      `${getConfig().API_URL}/audio/api/v1/admin/company_organisations`
    );
    const retrievedOrganisations = response?.data?.[0] || {};

    setOrganisations(retrievedOrganisations);
    setIsOrganisationsLoading(false);
  };

  const onCreateOrgItem = (orgKey) => async () => {
    if (creationInputName?.length <= 0) {
      return;
    }
    await myAxios.post(`${getConfig().API_URL}/audio/api/v1/admin/create_org_item`, {
      type: orgKey,
      name: creationInputName,
    });
    setCreationInputName("");
    fetchCompanyOrganisations();
  };

  useEffect(() => {
    if (!currentUser || currentUser === null) {
      return;
    }
    fetchCompanyOrganisations();
  }, [user]);

  const orgOptionsByKey = {
    department: (organisations?.departments || [])?.map((dpt) => ({
      label: dpt.name,
      key: dpt.id,
    })),
    localisation: (organisations?.localisations || [])?.map((loc) => ({
      label: loc.name,
      key: loc.id,
    })),
    team: (organisations?.teams || [])?.map((team) => ({
      label: team.name,
      key: team.id,
    })),
  };

  const onCreateUser = async () => {
    if (!isStep1Valid || !isStep2Valid) {
      return;
    }

    const dptName =
      orgOptionsByKey.department?.find((item) => item?.key === selectedDptId)?.label || null;
    const teamName =
      orgOptionsByKey.team?.find((item) => item?.key === selectedTeamId)?.label || null;
    const localisationName =
      orgOptionsByKey.localisation?.find((item) => item?.key === selectedLocId)?.label || null;

    setIsLoading(true);
    await myAxios.post(`${getConfig().API_URL}/audio/api/v1/admin/create_operator`, {
      first_name: firstnameInput,
      last_name: lastnameInput,
      email: emailInput,
      department_id: selectedDptId,
      department_name: dptName,
      team_id: selectedTeamId,
      team_name: teamName,
      localisation_id: selectedLocId,
      localisation_name: localisationName,
      ...(is3cx ? { extension_3cx: parseInt(extensionInput) } : {}),
      ...(isAircall ? { extension_aircall: parseInt(extensionAircallInput) } : {}),
      ...(isSftpHsf ? { cx_one_identifier: cxOneIdentifierInput } : {}),
      ...(phoneNumberInput?.length > 0 ? { telephone: phoneNumberInput } : {}),
    });

    handleOnClose();
  };

  const onCreateUserForOperator = async () => {
    if (!isExistingOperatorFormValid) {
      return;
    }

    setIsLoading(true);
    try {
      await myAxios.post(`${getConfig().API_URL}/audio/api/v1/admin/create_user_for_operator`, {
        operator_id: existingOperator.id,
        email: emailInput,
        first_name: firstnameInput,
        last_name: lastnameInput,
      });
      handleOnClose();
    } catch (error) {
      console.error("Error creating user for operator:", error);
      setIsLoading(false);
    }
  };

  const creationLabel = {
    department: "Create a new department",
    localisation: "Create a new localisation",
    team: "Create a new team",
  };

  const placeholders = {
    department: "Department",
    localisation: "Localisation",
    team: "Team",
  };

  const selectedKeyplayByOrgKey = {
    department: selectedDptId,
    localisation: selectedLocId,
    team: selectedTeamId,
  };

  const updateSelectedKeyplayByOrgKey = {
    department: setSelectedDptId,
    localisation: setSelectedLocId,
    team: setSelectedTeamId,
  };

  const renderOrgDropdown = (orgKey) => {
    const orgOptions = orgOptionsByKey[orgKey];

    orgOptions.sort((a, b) => {
      const cellValueA = a.label || "";
      const cellValueB = b.label || "";
      return cellValueA.localeCompare(cellValueB);
    });

    const selectedOrgId = selectedKeyplayByOrgKey[orgKey];
    const selectedOption = orgOptions.find((item) => item.key === selectedOrgId);

    return (
      <MHoverToggleMenu
        wrapperClassNames="w-full"
        floatingWrapperClassNames="w-[100%]"
        triggerComponent={
          <div className="cursor-pointer border-solid border-1 border-[#E5E5E5] rounded-md px-[12px] py-[8px] bg-white flex items-center justify-between">
            <span
              className={`text-xs ${selectedOption ? "text-[#171717]" : "text-[#171717]/[0.4]"}`}
            >
              {selectedOption?.label || placeholders[orgKey]}
            </span>
            <LuChevronsUpDown
              color="#171717"
              size={12}
            />
          </div>
        }
        items={orgOptions}
        selectedId={selectedKeyplayByOrgKey[orgKey]}
        setSelectedId={(key) => {
          updateSelectedKeyplayByOrgKey[orgKey](key);
        }}
        customRender={
          <div>
            <div className="flex items-center mb-2 text-sm">
              <BsPlus size={24} />
              <span className="whitespace-nowrap ml-1 mr-4">{creationLabel[orgKey]}</span>
            </div>
            <div className="flex items-center rounded-md border-2 border-[#E5E5E5] border-solid bg-white pl-2 text-sm">
              <input
                className="outline-none border-0 grow"
                placeholder={t("Name")}
                value={creationInputName}
                onChange={(e) => setCreationInputName(e.target.value)}
              />
              <div
                onClick={onCreateOrgItem(orgKey)}
                onKeyDown={onCreateOrgItem(orgKey)}
                className="cursor-pointer rounded-md border-2 border-[#E5E5E5] border-solid px-3 py-2"
                style={{ margin: "-2px" }}
              >
                {t("Create")}
              </div>
            </div>
          </div>
        }
      />
    );
  };

  const renderNewMemberStep1 = () => (
    <>
      <Input
        placeholder={t("First name")}
        radius="md"
        value={firstnameInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        onChange={(e) => setFirstNameInput(e.target.value)}
      />
      <Input
        placeholder={t("Last name")}
        radius="md"
        value={lastnameInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        onChange={(e) => setLastNameInput(e.target.value)}
      />
      <Input
        placeholder={t("Email")}
        type="email"
        radius="md"
        value={emailInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        endContent={
          <>
            {emailInput?.length > 0 && !isEmailValid && (
              <PiWarningCircle
                size={18}
                color="#EF4040"
              />
            )}
            {isEmailValid && (
              <IoIosCheckmarkCircleOutline
                size={16}
                color="#05CD99"
              />
            )}
          </>
        }
        onChange={(e) => setEmailInput(e.target.value)}
      />
      <Button
        onPress={() => setCurrentStep("details")}
        color="primary"
        isDisabled={!isStep1Valid}
        className="cursor-pointer h-[34px] px-[12px] w-full rounded-md flex items-center justify-center text-sm font-semibold text-white mb-[-8px]"
      >
        <span className="ml-[12px]">{t("Next")}</span>
        <FiArrowRight
          size={16}
          color="white"
          className="ml-2"
        />
      </Button>
      <Button
        onPress={handleOnClose}
        color="default"
        className="cursor-pointer h-[34px] px-[12px] w-full rounded-md flex items-center justify-center text-sm font-semibold text-[#171717] bg-white border-solid border-1 border-[#E5E5E5] hover:bg-[#F5F5F5]"
      >
        {t("Cancel")}
      </Button>
    </>
  );

  const renderNewMemberStep2 = () => (
    <>
      {renderOrgDropdown("department")}
      {renderOrgDropdown("team")}
      {renderOrgDropdown("localisation")}
      {is3cx && (
        <div className="flex flex-col items-start">
          <Input
            placeholder="3CX"
            radius="md"
            value={extensionInput}
            type="number"
            className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
            classNames={{
              inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
              input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
            }}
            endContent={
              <>
                {isExtensionCheckLoading && (
                  <CircularProgress
                    size="sm"
                    aria-label="Loading..."
                    color="primary"
                  />
                )}
                {isExtension3cxNotEmpty && !isExtensionCheckLoading && !isExtensionAvailable && (
                  <PiWarningCircle
                    size={18}
                    color="#EF4040"
                  />
                )}
                {isExtension3cxNotEmpty && !isExtensionCheckLoading && isExtensionAvailable && (
                  <IoIosCheckmarkCircleOutline
                    size={16}
                    color="#05CD99"
                  />
                )}
              </>
            }
            onChange={(e) => setExtensionInput(e.target.value)}
          />
          {isExtension3cxNotEmpty && !isExtensionCheckLoading && !isExtensionAvailable && (
            <div className="text-sm text-[#EF4040] mt-[4px]">
              {t("This extension is already in use")}
            </div>
          )}
        </div>
      )}
      {isAircall && (
        <div className="flex flex-col items-start">
          <Input
            placeholder="Extension Aircall"
            radius="md"
            value={extensionAircallInput}
            type="number"
            className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
            classNames={{
              inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
              input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
            }}
            onChange={(e) => setExtensionAircallInput(e.target.value)}
          />
        </div>
      )}
      {isSftpHsf && (
        <div className="flex flex-col items-start">
          <Input
            placeholder="CX One ID"
            radius="md"
            value={cxOneIdentifierInput}
            type="text"
            className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
            classNames={{
              inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
              input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
            }}
            endContent={
              <>
                {isExtensionCheckLoading && (
                  <CircularProgress
                    size="sm"
                    aria-label="Loading..."
                    color="primary"
                  />
                )}
                {isCxOneIdentifierNotEmpty && !isExtensionCheckLoading && !isExtensionAvailable && (
                  <PiWarningCircle
                    size={18}
                    color="#EF4040"
                  />
                )}
                {isCxOneIdentifierNotEmpty && !isExtensionCheckLoading && isExtensionAvailable && (
                  <IoIosCheckmarkCircleOutline
                    size={16}
                    color="#05CD99"
                  />
                )}
              </>
            }
            onChange={(e) => setCxOneIdentifierInput(e.target.value)}
          />
          {isCxOneIdentifierNotEmpty && !isExtensionCheckLoading && !isExtensionAvailable && (
            <div className="text-sm text-[#EF4040] mt-[4px]">
              {t("This identifier is already in use")}
            </div>
          )}
        </div>
      )}
      <Input
        placeholder={t("Phone number")}
        radius="md"
        value={phoneNumberInput}
        type="number"
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        onChange={(e) => setPhoneNumberInput(e.target.value)}
      />
      <Button
        onPress={onCreateUser}
        isDisabled={!isStep2Valid}
        className="cursor-pointer h-[34px] px-[12px] w-full bg-primary rounded-md flex items-center justify-center text-sm font-semibold text-white mb-[-8px]"
      >
        {isLoading && (
          <CircularProgress
            size="sm"
            aria-label="Loading..."
            color="primary"
          />
        )}
        <span className="ml-[12px]">{t("Send invitation")}</span>
        <IoMdSend
          size={16}
          color="white"
          className="ml-2"
        />
      </Button>
      <Button
        onPress={() => setCurrentStep("infos")}
        className="cursor-pointer h-[34px] px-[12px] w-full rounded-md flex items-center justify-center text-sm font-semibold text-[#171717] bg-white border-solid border-1 border-[#E5E5E5] hover:bg-[#F5F5F5]"
      >
        {t("Back")}
      </Button>
    </>
  );

  const renderExistingOperatorForm = () => (
    <>
      <Input
        placeholder={t("First name")}
        radius="md"
        value={firstnameInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        onChange={(e) => setFirstNameInput(e.target.value)}
      />
      <Input
        placeholder={t("Last name")}
        radius="md"
        value={lastnameInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        onChange={(e) => setLastNameInput(e.target.value)}
      />
      <Input
        placeholder={t("Email")}
        type="email"
        radius="md"
        value={emailInput}
        className="w-full border-solid border-1 border-[#E5E5E5] rounded-md h-[34px] min-h-[34px]"
        classNames={{
          inputWrapper: ["bg-white rounded-md shadow-none h-[32px] min-h-[32px]"],
          input: ["placeholder:text-[#404040]/[0.4] h-[34px] min-h-[34px]"],
        }}
        endContent={
          <>
            {emailInput?.length > 0 && !isEmailValid && (
              <PiWarningCircle
                size={18}
                color="#EF4040"
              />
            )}
            {isEmailValid && (
              <IoIosCheckmarkCircleOutline
                size={16}
                color="#05CD99"
              />
            )}
          </>
        }
        onChange={(e) => setEmailInput(e.target.value)}
      />
      <Button
        onPress={onCreateUserForOperator}
        isDisabled={!isExistingOperatorFormValid}
        className="cursor-pointer h-[34px] px-[12px] w-full bg-primary rounded-md flex items-center justify-center text-sm font-semibold text-white mb-[-8px]"
      >
        {isLoading && (
          <CircularProgress
            size="sm"
            aria-label="Loading..."
            color="primary"
          />
        )}
        <span className="ml-[12px]">{t("Send invitation")}</span>
        <IoMdSend
          size={16}
          color="white"
          className="ml-2"
        />
      </Button>
      <Button
        onPress={handleOnClose}
        color="default"
        className="cursor-pointer h-[34px] px-[12px] w-full rounded-md flex items-center justify-center text-sm font-semibold text-[#171717] bg-white border-solid border-1 border-[#E5E5E5] hover:bg-[#F5F5F5]"
      >
        {t("Cancel")}
      </Button>
    </>
  );

  const modalTitle = isLinkingExistingOperator
    ? t("Invite operator to your Workspace")
    : t("Invite a new member on your Workspace");

  const modalSubtitle = isLinkingExistingOperator
    ? t("Create an account for this operator by providing their email address")
    : t("Add a new member to your workspace by filling in the fields below");

  return (
    <div className="invitation-modal__wrapper">
      <div className="text-4xl text-bold mb-[24px]">{modalTitle}</div>
      <div className="text-xs mb-[24px]">{modalSubtitle}</div>
      <div className="flex flex-col gap-[20px] p-[48px] bg-white rounded-md text-[#171717]">
        {isLinkingExistingOperator && renderExistingOperatorForm()}
        {!isLinkingExistingOperator && currentStep === "infos" && renderNewMemberStep1()}
        {!isLinkingExistingOperator && currentStep === "details" && renderNewMemberStep2()}
      </div>
    </div>
  );
};

export default InvitationModal;
